<script setup lang="ts">
import autoAnimate from "@formkit/auto-animate";
import { computed, onMounted, ref, watch } from "vue";
import Icon from "../../components/icon/icon.vue";
import { useProductPageStore } from "../../stores/productPageStore";
import { USER_FEEDBACK } from "../../utils/API/APIErrors";
import { LineItemModel } from "../../utils/API/Cart/cartAPISchema";
import { formatWithoutRounding } from "../../utils/productPrice";
import { formatSize } from "../../utils/productSize";
import FeedbackMessage from "./FeedbackMessage.vue";
import { resolveStockPill } from "./resolveStockPill";
import StockStatusPill from "./StockStatusPill.vue";
import { useClickAndCollectLocationStore } from "./stores/clickAndCollectLocationStore";
import { useOrderTypeStore } from "./stores/orderTypeStore";
import { useShoppingCartStore } from "./stores/shoppingCartStore";

const props = defineProps<{
  lineItem: LineItemModel;
  error?: string;
  outOfStock?: boolean;
}>();

const shoppingCartStore = useShoppingCartStore();
const productPageStore = useProductPageStore();
const orderTypeStore = useOrderTypeStore();
const clickAndCollectLocationStore = useClickAndCollectLocationStore();
let abortController = new AbortController();
const root = ref();
const error = computed(() => {
  return props.error;
});
let isUpdatingFromApi = false;
const localQuantity = ref(props.lineItem.quantity);

/**
 * Resolves the stock-status pill for this line item.
 *
 * - Out-of-stock items get a warning pill with the provided error message
 *   (e.g. "product no longer available"), regardless of order type.
 * - For Click and Collect, the pill reflects `currentInventory` against
 *   `quantity` for the resolved click & collect store, naming the store.
 * - For Online, the pill reflects `currentInventory` against `quantity` for
 *   the default online warehouse, with generic "på lager" copy.
 */
const stockStatusPill = computed(() => {
  if (props.outOfStock) {
    return props.error ? { type: "warning" as const, message: props.error } : null;
  }

  const { currentInventory, quantity } = props.lineItem;

  if (orderTypeStore.isClickAndCollect) {
    const storeName = clickAndCollectLocationStore.storeName;
    if (!storeName) return null;
    return resolveStockPill(currentInventory, quantity, storeName);
  }

  return resolveStockPill(currentInventory, quantity);
});

// **Watch shoppingCartStore and sync localQuantity without recursion**
watch(
  () =>
    shoppingCartStore?.cart?.lineItems?.find(
      (item) => item.code === props.lineItem.code
    )?.quantity,
  (newQuantity) => {
    if (newQuantity === undefined || newQuantity === localQuantity.value) return;

    isUpdatingFromApi = true;
    localQuantity.value = newQuantity;
    isUpdatingFromApi = false;
  }
);

/**
 * Multiplies the list price of the line item by the quantity.
 *
 * @returns {number} The total list price.
 */
const listPriceTotal = computed(() => {
  return props.lineItem.listPrice.amount * localQuantity.value;
});

/**
 * Multiplies the discounted price of the line item by the quantity.
 *
 * @returns {number} The total discounted price.
 */
const discountedPriceTotal = computed(() => {
  return props.lineItem.discountedPrice.amount * localQuantity.value;
});

// If the discounted price is less than the list price, the product is on sale
const discountActive = computed(() => {
  return (
    props.lineItem.discountedPrice.amount < props.lineItem.listPrice.amount
  );
});

/**
 * The warehouse code to validate quantity changes against: the resolved
 * click & collect store's warehouse when Click and Collect is the active
 * order type, or undefined (default online warehouse) otherwise. Keeps
 * quantity updates consistent with the warehouse-aware inventory shown by
 * the stock-status pill.
 */
const activeWarehouseCode = computed(() =>
  orderTypeStore.isClickAndCollect ? clickAndCollectLocationStore.warehouseCode : undefined
);

/**
 * Whether increasing the quantity by one more would exceed the last fetched
 * stock level, reflecting `currentInventory` for whichever warehouse is
 * active (the resolved click & collect store, or the default online
 * warehouse).
 */
const isAtMaxStock = computed(() => {
  return localQuantity.value + 1 > props.lineItem.currentInventory;
});

/**
 * Validates the quantity of a line item.
 * The quantity must be greater than 0 and less than or equal to the current inventory.
 *
 * @param {number} quantity - The quantity to be validated.
 * @returns {boolean} - True if the quantity is valid, false otherwise.
 */
const increaseQuantity = async () => {
  if (localQuantity.value + 1 <= 0) return;
  if (isAtMaxStock.value) return;

  localQuantity.value++;
  productPageStore.incrementCartItemCount();

  if (abortController) abortController.abort();
  abortController = new AbortController();

  const result = await shoppingCartStore.updateProductQuantity(
    props.lineItem.code,
    props.lineItem.ean,
    1,
    true,
    abortController.signal,
    activeWarehouseCode.value,
  );

  if (!result.success) {
    localQuantity.value--;
    productPageStore.decrementCartItemCount();
  }
};

const decreaseQuantity = async () => {
  if (localQuantity.value - 1 <= 0) return;

  localQuantity.value--;
  productPageStore.decrementCartItemCount();

  if (abortController) abortController.abort();
  abortController = new AbortController();

  const result = await shoppingCartStore.updateProductQuantity(
    props.lineItem.code,
    props.lineItem.ean,
    -1,
    true,
    abortController.signal,
    activeWarehouseCode.value,
  );

  if (!result.success) {
    localQuantity.value++;
    productPageStore.incrementCartItemCount();
  }
};
/**
 * Deletes the product from the cart.
 */
const removeProduct = async () => {
  await shoppingCartStore.removeProduct(props.lineItem.code, activeWarehouseCode.value);
};

onMounted(() => {
  autoAnimate(root.value);
});
</script>

<template>
  <li
    ref="root"
    class="checkout-line-item"
    :class="{ 'checkout-line-item--out-of-stock': outOfStock }"
  >
    <div class="checkout-line-item__container">
      <div class="checkout-line-item__photo">
        <a :href="props.lineItem.productUrl">
          <img :src="props.lineItem.imageUrl" :alt="props.lineItem.altText" />
        </a>
      </div>
      <div class="checkout-line-item__content">
        <h3>
          <a class="checkout-line-item__name" :href="props.lineItem.productUrl">
            {{ props.lineItem.name }}
          </a>
          <span class="checkout-line-item__brand">
            ({{ props.lineItem.brand }})
          </span>
        </h3>
        <div class="checkout-line-item__details">
          <div class="checkout-line-item__price">
            <span
              v-if="discountActive"
              class="checkout-line-item__price--discounted"
            >
              {{ formatWithoutRounding(discountedPriceTotal) }}</span
            >
            <span
              :class="discountActive && 'checkout-line-item__price--original'"
            >
              {{ formatWithoutRounding(listPriceTotal) }}</span
            >
          </div>
          <div class="checkout-line-item__stock-status--desktop">
            <StockStatusPill
              v-if="stockStatusPill"
              :type="stockStatusPill.type"
              :message="stockStatusPill.message"
            />
          </div>
          <div class="checkout-line-item__size">
            <span class="checkout-line-item__size--label"
              >{{ $t("size") }}:
            </span>
            <span class="checkout-line-item__size--value">{{
              formatSize(lineItem.size)
            }}</span>
          </div>
          <div class="checkout-line-item__color">
            <span class="checkout-line-item__color--label"
              >{{ $t("color") }}:
            </span>
            <span class="checkout-line-item__color--value">{{
              props.lineItem.color
            }}</span>
          </div>
        </div>
        <div
          class="cart-item__actions checkout-line-item__quantity"
          :class="{ 'checkout-line-item__quantity--hidden': outOfStock }"
          :aria-hidden="outOfStock"
        >
          <input type="hidden" name="c" value="1933833" />
          <div
            class="line-item-select-quantity line-item-select-quantity--dark line-item-select-quantity--small"
          >
            <button
              class="line-item-select-quantity__button"
              :aria-label="$t('decreaseQuantity')"
              :disabled="outOfStock || localQuantity <= 1"
              :tabindex="outOfStock ? -1 : 0"
              @click="decreaseQuantity()"
            >
              -
            </button>
            <input
              v-model="localQuantity"
              type="number"
              class="line-item-select-quantity__input checkout-line-item__quantity-input"
              min="1"
              aria-live="assertive"
              aria-label="Antall"
              :readonly="true"
              :tabindex="outOfStock ? -1 : 0"
            />
            <button
              class="line-item-select-quantity__button"
              :aria-label="$t('increaseQuantity')"
              :disabled="outOfStock || isAtMaxStock"
              :tabindex="outOfStock || isAtMaxStock ? -1 : 0"
              @click="increaseQuantity()"
            >
              +
            </button>
          </div>
        </div>
      </div>
      <div class="checkout-line-item__remove-item">
        <button
          class="btn--icon"
          aria-label="fjern produkt fra handlekurv"
          @click="removeProduct()"
        >
          <Icon type="bin" size="checkout" />
        </button>
      </div>
    </div>
    <div class="checkout-line-item__trailer">
      <div class="checkout-line-item__stock-status--mobile">
        <StockStatusPill
          v-if="stockStatusPill"
          :type="stockStatusPill.type"
          :message="stockStatusPill.message"
        />
      </div>
      <div v-if="props.lineItem.memberPromotionsText" class="checkout-line-item__members-promotions-text">        
        <div v-if="props.lineItem.memberPromotionsLink">
          <a :href="props.lineItem.memberPromotionsLink">{{ props.lineItem.memberPromotionsText }}</a>
        </div>
        <div v-else>
          {{ props.lineItem.memberPromotionsText }}
        </div>        
      </div>
      <div v-if="props.lineItem.promotionsText"  class="checkout-line-item__promotions-text">
        {{ props.lineItem.promotionsText }}
      </div>
    </div>
    <FeedbackMessage v-if="!outOfStock" :message="error" type="error" />
  </li>
</template>
